import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";

/**
 * THE CLOSED KEY REGISTRY — SPEC-APP.md §1. Every piece of client-side URL
 * state in the app is one of these keys. None is a route. A key outside this
 * list is a type error, and no page keeps a `useState` drawer or a second
 * parser.
 */
export const URL_STATE_KEYS = [
  "drawer",
  "tab",
  "runId",
  "baseline",
  "subject",
  "subjectKind",
  "siteId",
  "fromSiteId",
  "toSiteId",
  "modeId",
  "carrierId",
  "productId",
  "from",
  "to",
  "state",
  /* Phase 20: the priority-policies scope chip; `SPEC-APP.md` §12.4's finding action links it. */
  "scope",
  "kind",
  "source",
  "resource",
  "severity",
  "disposition",
  "page",
  "q",
  "compareWith",
  /* Phase 24: the audit trail's six route filters. `from` and `to` are shared above. */
  "actor",
  "actorKind",
  "deliveryId",
  "resourceType",
  "resourceId",
  "action",
  /* Phase 29: the lane-days drift route filters by site CODE, and by one day. */
  "fromSiteCode",
  "toSiteCode",
  "since",
  /* Experiments (W4-2): the selected scenario node; `compareWith` above is its baseline. */
  "scenarioId",
  /* Analytics (W4-1): the spend screen's breakdown dimension, and the site CODE a finding's
     action prefills into the sites create dialog (`demand_rejected_unknown_site`). */
  "groupBy",
  "siteCode",
  /* Planning scopes (PS-D12): the selected planning scope. Absent means the default scope; a stale
     or unknown id is replaced by the default (`usePlanningScope`). Retained across navigation. */
  "scopeId",
] as const;

export type UrlStateKey = (typeof URL_STATE_KEYS)[number];

const REGISTRY = new Set<string>(URL_STATE_KEYS);

/** Runtime guard for the same closed registry the type enforces at compile time. */
export function isUrlStateKey(key: string): key is UrlStateKey {
  return REGISTRY.has(key);
}

/** The values a consumer reads: every key it named, present or `undefined`. */
export type UrlStateValues<K extends UrlStateKey> = Readonly<Record<K, string | undefined>>;

/** A write: a string sets, `null` or `undefined` removes. */
export type UrlStatePatch<K extends UrlStateKey> = Partial<Record<K, string | null | undefined>>;

export interface UrlState<K extends UrlStateKey> {
  values: UrlStateValues<K>;
  /** New history entry — the user can go back to the previous view. */
  push: (patch: UrlStatePatch<K>) => void;
  /** Same history entry — for keystroke-rate changes such as `q` or `page`. */
  replace: (patch: UrlStatePatch<K>) => void;
  /** Step back through the states this page pushed. */
  back: () => void;
}

function applyPatch(search: URLSearchParams, patch: Record<string, string | null | undefined>) {
  for (const [key, value] of Object.entries(patch)) {
    if (!isUrlStateKey(key)) {
      throw new Error(
        `useUrlState: "${key}" is not in the closed URL-state key registry (src/routes/urlState.ts).`,
      );
    }
    if (value === null || value === undefined || value === "") search.delete(key);
    else search.set(key, value);
  }
}

/**
 * The ONE owner of client-side URL state. A consumer names the keys it uses:
 *
 *   const { values, push, replace, back } = useUrlState(["drawer", "tab"]);
 */
export function useUrlState<const K extends readonly UrlStateKey[]>(keys: K): UrlState<K[number]> {
  const router = useRouter();
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const values = useMemo(() => {
    const params = new URLSearchParams(searchStr);
    const out = {} as Record<K[number], string | undefined>;
    for (const key of keys) out[key as K[number]] = params.get(key) ?? undefined;
    return out as UrlStateValues<K[number]>;
    // `keys` is a literal array at every call site; the joined form is its identity.
  }, [searchStr, keys.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const write = useCallback(
    (patch: UrlStatePatch<K[number]>, replace: boolean) => {
      const params = new URLSearchParams(searchStr);
      applyPatch(params, patch);
      const qs = params.toString();
      const href = qs ? `${pathname}?${qs}` : pathname;
      if (replace) router.history.replace(href);
      else router.history.push(href);
    },
    [router, searchStr, pathname],
  );

  const push = useCallback((patch: UrlStatePatch<K[number]>) => write(patch, false), [write]);
  const replace = useCallback((patch: UrlStatePatch<K[number]>) => write(patch, true), [write]);
  const back = useCallback(() => router.history.back(), [router]);

  return { values, push, replace, back };
}

/**
 * ONE URL-state value, readable WITHOUT a router above the caller. `useUrlState` needs a router
 * (`useRouterState` throws without one); this is for the few cross-cutting readers that sit under
 * hooks rendered in router-less contexts (the current-plan read is under `usePlanningScope`, and
 * is mounted by plain `QueryClientProvider` wrappers in tests). With no router the value is
 * `undefined` and `drop` does nothing. The key is still checked against the closed registry.
 */
export function useOptionalUrlValue(key: UrlStateKey): {
  value: string | undefined;
  /** Removes the key from the URL, in place (no new history entry). */
  drop: () => void;
} {
  const router = useRouter({ warn: false });
  const subscribe = useCallback(
    (onChange: () => void) => (router ? router.history.subscribe(onChange) : () => {}),
    [router],
  );
  const search = useSyncExternalStore(
    subscribe,
    () => (router ? router.history.location.search : ""),
    () => "",
  );
  const value = useMemo(() => new URLSearchParams(search).get(key) ?? undefined, [search, key]);
  const drop = useCallback(() => {
    if (!router) return;
    const { pathname, search: current } = router.history.location;
    const params = new URLSearchParams(current);
    params.delete(key);
    const qs = params.toString();
    router.history.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [router, key]);
  return { value, drop };
}

/**
 * THE SCOPE SURVIVES NAVIGATION (PS-D12). A router search middleware, mounted once on the root
 * route: every link and `navigate` the router builds carries the current `scopeId` forward unless
 * the destination names one itself. A tenant on its default scope has no `scopeId` in the URL, so
 * there is nothing to carry and its links are unchanged. (Writes through `useUrlState` edit the
 * current search string in place, so they keep the key without this.)
 */
export function retainScopeId({
  search,
  next,
}: {
  search: Record<string, unknown>;
  next: (newSearch: Record<string, unknown>) => Record<string, unknown>;
}): Record<string, unknown> {
  const result = next(search);
  if ("scopeId" in result || search["scopeId"] === undefined) return result;
  return { ...result, scopeId: search["scopeId"] };
}
